import {
  InputAdornment,
  Typography,
  TypographyVariant,
  useTheme,
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { MoneyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  DefinitiveDecimalTextField,
  DefinitiveDecimalTextFieldProps,
} from './DefinitiveDecimalTextField'

type DefinitiveMoneyFieldProps = {
  moneyDto: MoneyDto
  onChange: (value: MoneyDto) => void
  validation?: (value: MoneyDto) => string[]
  showRawValueInTooltip?: (value: MoneyDto) => string
  typographyVariant: TypographyVariant
  disabled?: boolean
} & Omit<
  DefinitiveDecimalTextFieldProps,
  'value' | 'onChange' | 'showRawValueInTooltip'
>

export const DefinitiveMoneyField = (props: DefinitiveMoneyFieldProps) => {
  const [currentMoney, setCurrentMoney] = useState(props.moneyDto)
  const [currentValidations, setCurrentValidations] =
    useState<string[]>(undefined)

  const theme = useTheme()

  useEffect(() => {
    setCurrentMoney(props.moneyDto)
  }, [props.moneyDto])

  if (!props.moneyDto || !currentMoney) {
    return null
  }

  const handleChange = (field: keyof MoneyDto, value: number | string) => {
    const newQuantity = {
      ...currentMoney,
      [field]: value,
    }

    setCurrentMoney(newQuantity)

    const validations = props.validation
      ? props.validation(newQuantity)
      : undefined

    if (validations?.length > 0) {
      setCurrentValidations(validations)
    } else {
      setCurrentValidations(undefined)
      props.onChange(newQuantity)
    }
  }

  return (
    <DefinitiveDecimalTextField
      {...props}
      numberFormatProps={{
        ...props.numberFormatProps,
        decimalScale: props.numberFormatProps?.decimalScale ?? 2,
        fixedDecimalScale: true,
        allowNegative: props.numberFormatProps?.allowNegative ?? false,
      }}
      textFieldProps={{
        inputProps: {
          style: {
            ...(props.textFieldProps?.inputProps?.style || {}),
            textAlign:
              props.textFieldProps?.inputProps?.style?.textAlign || 'right',
            fontSize:
              theme.typography[props.typographyVariant || 'caption'].fontSize,
            padding:
              props.textFieldProps?.inputProps?.style?.padding || '.1em .1em',
            paddingRight: props.textFieldProps?.inputProps?.style?.paddingRight,
          },
        },
        InputProps: {
          ...(props.textFieldProps?.InputProps || {}),
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant={props.typographyVariant || 'inherit'}>
                {props.moneyDto.symbol}
              </Typography>
            </InputAdornment>
          ),
        },
        disabled: props.disabled,
      }}
      maxValue={props.maxValue}
      minValue={props.minValue}
      value={currentMoney?.value}
      onChange={(value) => handleChange('value', value)}
      warnings={currentValidations}
      showRawValueInTooltip={
        props.showRawValueInTooltip &&
        ((value) => props.showRawValueInTooltip({ ...props.moneyDto, value }))
      }
      isAllowed={props.isAllowed}
    />
  )
}
